import type { InstrumentationConfig, InstrumentationModuleDefinition } from '@opentelemetry/instrumentation';
import { InstrumentationBase, InstrumentationNodeModuleDefinition } from '@opentelemetry/instrumentation';
import {
  _INTERNAL_cleanupToolCallSpanContext,
  _INTERNAL_getSpanContextForToolCallId,
  addNonEnumerableProperty,
  captureException,
  getActiveSpan,
  getClient,
  handleCallbackErrors,
  SDK_VERSION,
  withScope,
} from '@sentry/core';
import { INTEGRATION_NAME } from './constants';
import type { TelemetrySettings, VercelAiIntegration } from './types';

const SUPPORTED_VERSIONS = ['>=3.0.0 <7'];

// List of patched methods
// From: https://sdk.vercel.ai/docs/ai-sdk-core/telemetry#collected-data
const INSTRUMENTED_METHODS = [
  'generateText',
  'streamText',
  'generateObject',
  'streamObject',
  'embed',
  'embedMany',
  'rerank',
] as const;

interface MethodFirstArg extends Record<string, unknown> {
  experimental_telemetry?: TelemetrySettings;
}

type MethodArgs = [MethodFirstArg, ...unknown[]];

type PatchedModuleExports = Record<(typeof INSTRUMENTED_METHODS)[number], (...args: MethodArgs) => unknown> &
  Record<string, unknown>;

interface RecordingOptions {
  recordInputs?: boolean;
  recordOutputs?: boolean;
}

interface ToolError {
  type: 'tool-error' | 'tool-result' | 'tool-call';
  toolCallId: string;
  toolName: string;
  input?: {
    [key: string]: unknown;
  };
  error: Error;
  dynamic?: boolean;
}

function isToolError(obj: unknown): obj is ToolError {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  const candidate = obj as Record<string, unknown>;
  return (
    'type' in candidate &&
    'error' in candidate &&
    'toolName' in candidate &&
    'toolCallId' in candidate &&
    candidate.type === 'tool-error' &&
    candidate.error instanceof Error
  );
}

/**
 * Process tool call results: capture tool errors and clean up span context mappings.
 *
 * Error checking runs first (needs span context for linking), then cleanup removes all entries.
 * Tool errors are not rejected in Vercel AI V5 — they appear as metadata in the result content.
 */
export function processToolCallResults(result: unknown): void {
  if (typeof result !== 'object' || result === null || !('content' in result)) {
    return;
  }

  const resultObj = result as { content: Array<object> };
  if (!Array.isArray(resultObj.content)) {
    return;
  }

  captureToolErrors(resultObj.content);
  cleanupToolCallSpanContexts(resultObj.content);
}

function captureToolErrors(content: Array<object>): void {
  for (const item of content) {
    if (!isToolError(item)) {
      continue;
    }

    // Try to get the span context associated with this tool call ID
    const spanContext = _INTERNAL_getSpanContextForToolCallId(item.toolCallId);

    if (spanContext) {
      // We have the span context, so link the error using span and trace IDs
      withScope(scope => {
        scope.setContext('trace', {
          trace_id: spanContext.traceId,
          span_id: spanContext.spanId,
        });

        scope.setTag('vercel.ai.tool.name', item.toolName);
        scope.setTag('vercel.ai.tool.callId', item.toolCallId);
        scope.setLevel('error');

        captureException(item.error, {
          mechanism: {
            type: 'auto.vercelai.otel',
            handled: false,
          },
        });
      });
    } else {
      // Fallback: capture without span linking
      withScope(scope => {
        scope.setTag('vercel.ai.tool.name', item.toolName);
        scope.setTag('vercel.ai.tool.callId', item.toolCallId);
        scope.setLevel('error');

        captureException(item.error, {
          mechanism: {
            type: 'auto.vercelai.otel',
            handled: false,
          },
        });
      });
    }
  }
}

/**
 * Remove span context entries for all completed tool calls in the content array.
 */
export function cleanupToolCallSpanContexts(content: Array<object>): void {
  for (const item of content) {
    if (
      typeof item === 'object' &&
      item !== null &&
      'toolCallId' in item &&
      typeof (item as Record<string, unknown>).toolCallId === 'string'
    ) {
      _INTERNAL_cleanupToolCallSpanContext((item as Record<string, unknown>).toolCallId as string);
    }
  }
}

/**
 * Determines whether to record inputs and outputs for Vercel AI telemetry based on the configuration hierarchy.
 *
 * The order of precedence is:
 * 1. The vercel ai integration options
 * 2. The experimental_telemetry options in the vercel ai method calls
 * 3. When telemetry is explicitly enabled (isEnabled: true), default to recording
 * 4. Otherwise, use the dataCollection.genAI settings from client options
 */
export function determineRecordingSettings(
  integrationRecordingOptions: RecordingOptions | undefined,
  methodTelemetryOptions: RecordingOptions,
  telemetryExplicitlyEnabled: boolean | undefined,
  defaultInputsEnabled: boolean,
  defaultOutputsEnabled: boolean,
): { recordInputs: boolean; recordOutputs: boolean } {
  const recordInputs =
    integrationRecordingOptions?.recordInputs !== undefined
      ? integrationRecordingOptions.recordInputs
      : methodTelemetryOptions.recordInputs !== undefined
        ? methodTelemetryOptions.recordInputs
        : telemetryExplicitlyEnabled === true
          ? true // When telemetry is explicitly enabled, default to recording inputs
          : defaultInputsEnabled;

  const recordOutputs =
    integrationRecordingOptions?.recordOutputs !== undefined
      ? integrationRecordingOptions.recordOutputs
      : methodTelemetryOptions.recordOutputs !== undefined
        ? methodTelemetryOptions.recordOutputs
        : telemetryExplicitlyEnabled === true
          ? true // When telemetry is explicitly enabled, default to recording outputs
          : defaultOutputsEnabled;

  return { recordInputs, recordOutputs };
}

/**
 * This detects is added by the Sentry Vercel AI Integration to detect if the integration should
 * be enabled.
 *
 * It also patches the `ai` module to enable Vercel AI telemetry automatically for all methods.
 */
export class SentryVercelAiInstrumentation extends InstrumentationBase {
  private _isPatched = false;
  private _callbacks: (() => void)[] = [];

  public constructor(config: InstrumentationConfig = {}) {
    super('@sentry/instrumentation-vercel-ai', SDK_VERSION, config);
  }

  /**
   * Initializes the instrumentation by defining the modules to be patched.
   */
  public init(): InstrumentationModuleDefinition {
    const module = new InstrumentationNodeModuleDefinition('ai', SUPPORTED_VERSIONS, this._patch.bind(this));
    return module;
  }

  /**
   * Call the provided callback when the module is patched.
   * If it has already been patched, the callback will be called immediately.
   */
  public callWhenPatched(callback: () => void): void {
    if (this._isPatched) {
      callback();
    } else {
      this._callbacks.push(callback);
    }
  }

  /**
   * Patches module exports to enable Vercel AI telemetry.
   */
  private _patch(moduleExports: PatchedModuleExports): unknown {
    this._isPatched = true;

    this._callbacks.forEach(callback => callback());
    this._callbacks = [];

    const generatePatch = <T extends (...args: MethodArgs) => unknown>(originalMethod: T): T => {
      return new Proxy(originalMethod, {
        apply: (target, thisArg, args: MethodArgs) => {
          const existingExperimentalTelemetry = args[0].experimental_telemetry || {};
          const isEnabled = existingExperimentalTelemetry.isEnabled;

          const client = getClient();
          const integration = client?.getIntegrationByName<VercelAiIntegration>(INTEGRATION_NAME);
          const integrationOptions = integration?.options;
          const genAI = integration ? client?.getDataCollectionOptions().genAI : undefined;

          const { recordInputs, recordOutputs } = determineRecordingSettings(
            integrationOptions,
            existingExperimentalTelemetry,
            isEnabled,
            Boolean(genAI?.inputs),
            Boolean(genAI?.outputs),
          );

          args[0].experimental_telemetry = {
            ...existingExperimentalTelemetry,
            isEnabled: isEnabled !== undefined ? isEnabled : true,
            recordInputs,
            recordOutputs,
          };

          return handleCallbackErrors(
            () => Reflect.apply(target, thisArg, args),
            error => {
              // This error bubbles up to unhandledrejection handler (if not handled before),
              // where we do not know the active span anymore
              // So to circumvent this, we set the active span on the error object
              // which is picked up by the unhandledrejection handler
              if (error && typeof error === 'object') {
                addNonEnumerableProperty(error, '_sentry_active_span', getActiveSpan());
              }
            },
            () => {},
            result => {
              processToolCallResults(result);
            },
          );
        },
      });
    };

    // Is this an ESM module?
    // https://tc39.es/ecma262/#sec-module-namespace-objects
    if (Object.prototype.toString.call(moduleExports) === '[object Module]') {
      // In ESM we take the usual route and just replace the exports we want to instrument
      for (const method of INSTRUMENTED_METHODS) {
        // Skip methods that don't exist in this version of the AI SDK (e.g., rerank was added in v6)
        if (moduleExports[method] != null) {
          moduleExports[method] = generatePatch(moduleExports[method]);
        }
      }

      return moduleExports;
    } else {
      // In CJS we can't replace the exports in the original module because they
      // don't have setters, so we create a new object with the same properties
      const patchedModuleExports = INSTRUMENTED_METHODS.reduce((acc, curr) => {
        // Skip methods that don't exist in this version of the AI SDK (e.g., rerank was added in v6)
        if (moduleExports[curr] != null) {
          acc[curr] = generatePatch(moduleExports[curr]);
        }
        return acc;
      }, {} as PatchedModuleExports);

      return { ...moduleExports, ...patchedModuleExports };
    }
  }
}
